import { useState, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Bell,
  AlertTriangle,
  Clock,
  CheckCircle2,
  Users,
  FileText,
  TrendingUp,
  Sparkles,
  X,
  Settings,
  BellOff,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNotifications } from '@/hooks/useNotifications';
import { useAttention } from '@/hooks/useAttention';
import { useAuth } from '@/hooks/useAuth';
import { useLocalization } from '@/hooks/useLocalization';

interface Notification {
  id: string;
  type: 'deadline' | 'risk' | 'update' | 'mention' | 'task' | 'milestone';
  priority: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  time: string;
  read: boolean;
  aiGenerated?: boolean;
  actionUrl?: string;
}

const priorityConfig = {
  critical: {
    color: 'bg-red-500',
    textColor: 'text-red-500',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/20',
    label: 'Critical',
  },
  high: {
    color: 'bg-amber-500',
    textColor: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/20',
    label: 'High',
  },
  medium: {
    color: 'bg-blue-500',
    textColor: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/20',
    label: 'Medium',
  },
  low: {
    color: 'bg-muted-foreground',
    textColor: 'text-muted-foreground',
    bgColor: 'bg-muted',
    borderColor: 'border-border',
    label: 'Low',
  },
};

const typeIcons: Record<string, React.ElementType> = {
  deadline: Clock,
  risk: AlertTriangle,
  update: FileText,
  mention: Users,
  task: CheckCircle2,
  milestone: TrendingUp,
};

interface NotificationBellProps {
  onNavigate?: (view: string) => void;
}

const NotificationBell = ({ onNavigate }: NotificationBellProps) => {
  const { tx, t, ago } = useLocalization();
  const { user } = useAuth();
  const {
    notifications: realNotifications,
    markRead,
    markAllRead,
    dismiss,
  } = useNotifications();
  const attention = useAttention();
  const [isOpen, setIsOpen] = useState(false);

  // Live attention items are computed, not stored, so their read/dismissed
  // state is per viewer in localStorage. State is keyed by id + signature:
  // when the underlying count changes (e.g. 2 overdue tasks becomes 5) the
  // item reads as new again instead of staying silenced.
  const storeKey = `masira.attention.${user?.id ?? 'anon'}`;
  const [seen, setSeen] = useState<Record<string, 'read' | 'dismissed'>>(() => {
    try { return JSON.parse(localStorage.getItem(storeKey) ?? '{}'); } catch { return {}; }
  });
  const mark = useCallback((keys: string[], state: 'read' | 'dismissed') => {
    setSeen((prev) => {
      const next = { ...prev };
      keys.forEach((k) => { if (!(next[k] === 'dismissed')) next[k] = state; });
      try { localStorage.setItem(storeKey, JSON.stringify(next)); } catch { /* storage unavailable */ }
      return next;
    });
  }, [storeKey]);

  const live: Notification[] = useMemo(
    () => attention.items
      .filter((i) => seen[`${i.id}:${i.signature}`] !== 'dismissed')
      .map((i) => ({
        id: `live:${i.id}:${i.signature}`,
        type: i.type,
        priority: i.priority,
        title: i.title,
        description: i.body,
        time: 'Live',
        read: seen[`${i.id}:${i.signature}`] === 'read',
        actionUrl: i.view,
      })),
    [attention.items, seen],
  );

  const stored: Notification[] = useMemo(
    () => realNotifications.map((n) => ({
      id: n.id,
      type: n.type,
      priority: n.priority,
      title: n.title,
      description: n.body || '',
      time: ago(n.created_at),
      read: n.read,
      actionUrl: n.link_view || undefined,
    })),
    [realNotifications, ago],
  );

  const notifications = useMemo(() => [...live, ...stored], [live, stored]);

  const unreadCount = notifications.filter(n => !n.read).length;
  const criticalCount = notifications.filter(n => !n.read && n.priority === 'critical').length;

  const isLive = (id: string) => id.startsWith('live:');
  const liveKey = (id: string) => id.slice('live:'.length);

  const markAsRead = (id: string) => {
    if (isLive(id)) mark([liveKey(id)], 'read');
    else markRead(id);
  };

  const markAllAsRead = () => {
    mark(live.map((n) => liveKey(n.id)), 'read');
    markAllRead();
  };

  const dismissNotification = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLive(id)) mark([liveKey(id)], 'dismissed');
    else dismiss(id);
  };

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    if (notification.actionUrl) {
      onNavigate?.(notification.actionUrl);
      setIsOpen(false);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className={cn(
              "absolute -top-1 -end-1 h-5 w-5 rounded-full flex items-center justify-center text-xs font-medium text-white",
              criticalCount > 0 ? "bg-red-500 animate-pulse" : "bg-primary"
            )}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-foreground">{t('Notifications')}</h3>
            {unreadCount > 0 && (
              <Badge variant="secondary">{unreadCount}  {t('new')}</Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={markAllAsRead}>
                
                {t('Mark all read')}
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t('Notification settings')} disabled>
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* AI Priority Banner */}
        {criticalCount > 0 && (
          <div className="px-4 py-2 bg-red-500/10 border-b border-red-500/20 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-red-500" />
            <span className="text-sm text-red-500 font-medium">
              {criticalCount}  {t('critical alert')}{criticalCount > 1 ? 's' : ''}  {t('requiring attention')}
            </span>
          </div>
        )}

        {/* Notifications List */}
        <ScrollArea className="h-[400px]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-8 text-muted-foreground">
              <BellOff className="h-12 w-12 mb-3" />
              <p className="font-medium">{t('All caught up')}</p>
              <p className="text-sm">{t('No critical risks, pending decisions or overdue work')}</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((notification) => {
                const config = priorityConfig[notification.priority];
                const Icon = typeIcons[notification.type] || Bell;

                return (
                  <div
                    key={notification.id}
                    className={cn(
                      "p-4 hover:bg-muted/50 cursor-pointer transition-colors relative group",
                      !notification.read && "bg-primary/5"
                    )}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex gap-3">
                      {/* Priority indicator */}
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                        config.bgColor
                      )}>
                        <Icon className={cn("h-5 w-5", config.textColor)} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <p className={cn(
                              "font-medium text-sm text-foreground",
                              !notification.read && "font-semibold"
                            )}>
                              {tx(notification.title)}
                            </p>
                            {notification.aiGenerated && (
                              <Sparkles className="h-3 w-3 text-primary flex-shrink-0" />
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => dismissNotification(notification.id, e)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {tx(notification.description)}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-muted-foreground">
                            {notification.time}
                          </span>
                          <Badge 
                            variant="outline" 
                            className={cn("text-xs", config.textColor, config.borderColor)}
                          >
                            {tx(config.label)}
                          </Badge>
                        </div>
                      </div>

                      {/* Unread indicator */}
                      {!notification.read && (
                        <div className={cn("w-2 h-2 rounded-full flex-shrink-0 mt-2", config.color)} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="p-3 border-t border-border bg-muted/30">
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Sparkles className="h-3 w-3" />
            <span>{t('Only items that need action: risks, decisions, approvals and overdue work')}</span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
